import { useMemo } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { commandCenterApi } from '../../services/api'
import { Button, StatePanel } from '../common'
import styles from '../CommandCenterPostCreator.module.css'

const getMediaAssetId = (media) => media?.mediaId || media?.asset_id || media?.id

export default function OrgMediaOverridePicker({
  org,
  selectedMedia = [],
  onToggleAsset,
}) {
  const orgSlug = org?.slug

  const {
    data,
    error,
    isLoading,
    isError,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
    refetch,
  } = useInfiniteQuery({
    queryKey: ['cc-post-override-library-assets', orgSlug],
    queryFn: ({ pageParam = null }) => commandCenterApi.listOrgMediaAssets(orgSlug, {
      source: 'all',
      ...(pageParam ? { cursor: pageParam } : {}),
      include_associations: false,
    }),
    initialPageParam: null,
    getNextPageParam: (lastPage) => lastPage?.next_cursor ?? undefined,
    enabled: Boolean(orgSlug),
    staleTime: 30_000,
  })

  const fetchedItems = useMemo(() => {
    return data?.pages?.flatMap((page) => page?.items || []) || []
  }, [data])

  // Preserve chosen existing media: if an asset was previously chosen
  // (e.g. from an earlier page or pre-existing override), ensure it is present
  // in the displayed choices so operators never lose visibility of selected media.
  const displayAssets = useMemo(() => {
    const map = new Map()
    for (const item of fetchedItems) {
      if (item?.id) {
        map.set(item.id, item)
      }
    }
    for (const media of selectedMedia) {
      const id = getMediaAssetId(media)
      if (id && !map.has(id)) {
        map.set(id, {
          id,
          title: media.title || media.name || 'Selected media',
          asset_type: media.type || media.asset_type,
          url: media.url,
          object_key: media.object_key,
          source_object_key: media.source_object_key,
        })
      }
    }
    return Array.from(map.values())
  }, [fetchedItems, selectedMedia])

  if (isLoading && !displayAssets.length) {
    return <StatePanel compact state="loading" title="Loading book media" />
  }

  if (isError && displayAssets.length === 0) {
    return (
      <StatePanel
        compact
        state="error"
        title="Could not load book media"
        description={error?.message}
        action={<Button variant="secondary" onClick={() => refetch()}>Retry</Button>}
      />
    )
  }

  if (displayAssets.length === 0) {
    return <StatePanel compact state="empty" title="No media in this book" />
  }

  return (
    <div className={styles.overrideMediaPicker || ''}>
      <div className={styles.chipGroup}>
        {displayAssets.map((asset) => {
          const selected = selectedMedia.some((media) => (
            getMediaAssetId(media) === asset.id
          ))
          return (
            <label key={asset.id} className={`${styles.chip} ${selected ? styles.chipActive : ''}`}>
              <input
                type="checkbox"
                checked={selected}
                onChange={() => onToggleAsset(asset)}
              />
              {asset.title || asset.name || 'Selected media'}
            </label>
          )
        })}
      </div>
      {hasNextPage || isFetchNextPageError || (isError && displayAssets.length > 0) ? (
        <div style={{ marginTop: '0.5rem' }}>
          {isFetchNextPageError || isError ? (
            <Button
              type="button"
              variant="secondary"
              onClick={() => fetchNextPage()}
            >
              Retry loading more
            </Button>
          ) : (
            <Button
              type="button"
              variant="secondary"
              onClick={() => fetchNextPage()}
              loading={isFetchingNextPage}
              disabled={isFetchingNextPage}
            >
              Load more
            </Button>
          )}
        </div>
      ) : null}
    </div>
  )
}
